<script setup lang="ts">
/**
 * ResistTable — Shield/Armor/Hull resistance grid (EM, Therm, Kin, Exp)
 * Shows resistance % with color-coded bars.
 * Calculator already returns values as percentages (0-100).
 */
import type { DefenseStats } from '../../../shared/types';

const props = defineProps<{
  defense: DefenseStats;
}>();

const dmgTypes: { key: 'em' | 'thermal' | 'kinetic' | 'explosive'; label: string; color: string }[] = [
  { key: 'em', label: 'EM', color: 'var(--em-color)' },
  { key: 'thermal', label: 'Th', color: 'var(--therm-color)' },
  { key: 'kinetic', label: 'Kn', color: 'var(--kin-color)' },
  { key: 'explosive', label: 'Ex', color: 'var(--exp-color)' },
];

const layers: { key: 'shield' | 'armor' | 'hull'; label: string; color: string }[] = [
  { key: 'shield', label: 'S', color: 'var(--shield-color)' },
  { key: 'armor', label: 'A', color: 'var(--armor-color)' },
  { key: 'hull', label: 'H', color: 'var(--hull-color)' },
];

function getResist(layer: 'shield' | 'armor' | 'hull', dt: 'em' | 'thermal' | 'kinetic' | 'explosive'): number {
  return props.defense[layer].resists[dt];
}

function resistColor(pct: number): string {
  if (pct >= 70) return '#4caf50';
  if (pct >= 40) return '#ff9800';
  if (pct >= 20) return '#ff5722';
  return '#f44336';
}
</script>

<template>
  <div class="resist-table">
    <!-- Column headers -->
    <div class="resist-header">
      <span class="resist-corner"></span>
      <span
        v-for="dt in dmgTypes"
        :key="dt.key"
        class="resist-col-header"
        :style="{ color: dt.color }"
      >{{ dt.label }}</span>
    </div>

    <!-- Rows -->
    <div
      v-for="layer in layers"
      :key="layer.key"
      class="resist-row"
    >
      <span class="resist-row-label" :style="{ color: layer.color }">{{ layer.label }}</span>
      <span
        v-for="dt in dmgTypes"
        :key="dt.key"
        class="resist-cell"
        :title="`${layer.label === 'S' ? 'Shield' : layer.label === 'A' ? 'Armor' : 'Hull'} ${dt.label}: ${getResist(layer.key, dt.key).toFixed(1)}%`"
      >
        <span class="resist-bar-bg">
          <span
            class="resist-bar-fill"
            :style="{
              width: Math.min(getResist(layer.key, dt.key), 100) + '%',
              background: resistColor(getResist(layer.key, dt.key)),
            }"
          ></span>
        </span>
        <span class="resist-num">{{ getResist(layer.key, dt.key).toFixed(0) }}</span>
      </span>
    </div>
  </div>
</template>

<style scoped>
.resist-table {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.resist-header {
  display: grid;
  grid-template-columns: 18px repeat(4, 1fr);
  gap: 3px;
  padding: 0 0 2px;
}

.resist-corner {
  /* empty spacer for row labels */
}

.resist-col-header {
  font-size: 0.78rem;
  font-weight: 600;
  text-align: center;
}

.resist-row {
  display: grid;
  grid-template-columns: 18px repeat(4, 1fr);
  gap: 3px;
  align-items: center;
}

.resist-row-label {
  font-size: 0.78rem;
  font-weight: 600;
}

.resist-cell {
  display: flex;
  flex-direction: column;
  gap: 1px;
  align-items: center;
}

.resist-bar-bg {
  width: 100%;
  height: 4px;
  background: rgba(42, 63, 95, 0.4);
  border-radius: 2px;
  overflow: hidden;
}

.resist-bar-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.resist-num {
  font-size: 0.78rem;
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
</style>
